import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, Switch, Typography, InputNumber } from 'antd';
import { createSpecialty, updateSpecialty, getSpecialtyById } from '../../services/specialties';
import MediaUpload from '../../components/MediaUpload/MediaUpload';

const { useApp } = App;
const { TextArea } = Input;
const { Text } = Typography;

const SpecialtyUpsertPage = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);

    const navigate = useNavigate();
    const { message } = useApp();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [specialtyData, setSpecialtyData] = useState(null);
    const [iconFile, setIconFile] = useState(null);
    const [iconRemoved, setIconRemoved] = useState(false);

    useEffect(() => {
        if (!isEdit || !id) return;

        setLoading(true);
        getSpecialtyById(id)
            .then((res) => {
                const specialty = res.specialty || res;
                setSpecialtyData(specialty);
                form.setFieldsValue({
                    displayName: specialty.displayName || specialty.label,
                    description: specialty.description || '',
                    order: specialty.order || 0,
                    isActive: specialty.isActive,
                });
            })
            .catch((err) => {
                console.error(err);
                message.error('No se pudo cargar la especialidad');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, isEdit, form, message]);

    const onSubmit = async (values) => {
        try {
            setLoading(true);

            const formData = new FormData();
            const displayName = values.displayName.trim();
            const name = displayName.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');

            formData.append('displayName', displayName);
            formData.append('name', name);
            formData.append('description', values.description?.trim() || '');
            formData.append('order', Number(values.order) || 0);
            formData.append('isActive', values.isActive);

            if (iconFile) {
                formData.append('icon', iconFile);
            } else if (iconRemoved) {
                formData.append('icon', ''); // O null según el backend soporte
            }

            if (isEdit && id) {
                await updateSpecialty(id, formData);
                message.success('Especialidad actualizada correctamente');
            } else {
                await createSpecialty(formData);
                message.success('Especialidad creada correctamente');
            }

            navigate('/specialties/all');
        } catch (err) {
            console.error(err);
            const errorMessage = err?.__normalized?.message || 'Error al guardar la especialidad';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={isEdit ? 'Editar Especialidad' : 'Crear Especialidad'}
                loading={loading}
                style={{ borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    autoComplete="off"
                    initialValues={{
                        isActive: true,
                    }}
                >
                    <Row gutter={24}>
                        <Col xs={24} md={16}>
                            <Row gutter={16}>
                                <Col xs={24} md={24}>
                                    <Form.Item
                                        label="Nombre de la Especialidad"
                                        name="displayName"
                                        rules={[
                                            { required: true, message: 'El nombre es requerido' },
                                            { min: 2, message: 'Mínimo 2 caracteres' },
                                        ]}
                                    >
                                        <Input placeholder="Ej: Cardiología" allowClear />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Descripción"
                                        name="description"
                                    >
                                        <TextArea
                                            placeholder="Breve descripción de la especialidad"
                                            rows={4}
                                            allowClear
                                            showCount
                                            maxLength={500}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Activo"
                                name="isActive"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>

                            <Form.Item
                                label="Orden"
                                name="order"
                                rules={[{ type: 'number', min: 0, message: 'El orden debe ser un número positivo' }]}
                                extra="Define la posición en el listado (0 es el primero)"
                            >
                                <InputNumber min={0} style={{ width: 120 }} placeholder="0" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <MediaUpload
                                label="Icono (PNG)"
                                allowVideo={false}
                                name="icon"
                                originalMediaPath={specialtyData?.icon}
                                isEdit={isEdit}
                                onChange={(info) => {
                                    setIconFile(info.file);
                                    setIconRemoved(info.removed);
                                }}
                            />
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '-10px' }}>
                                Se recomienda un archivo PNG con fondo transparente.
                            </Text>
                        </Col>
                    </Row>

                    <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
                        <Col>
                            <Button onClick={() => navigate(-1)} disabled={loading}>
                                Cancelar
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                style={{
                                    borderRadius: '10px',
                                    height: '45px',
                                    padding: '0 30px',
                                    backgroundColor: '#1890ff',
                                    borderColor: '#1890ff',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }}
                            >
                                {isEdit ? 'Guardar cambios' : 'Crear Especialidad'}
                            </Button>
                        </Col>
                    </Row>

                </Form>
            </Card>
        </div>
    );
};

export default SpecialtyUpsertPage;
